import {
  ApplicationConfig,
  APP_INITIALIZER,
  ErrorHandler,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { routes } from './app.routes';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { provideNzConfig } from 'ng-zorro-antd/core/config';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzModalService } from 'ng-zorro-antd/modal';
import { RolePermissionService } from './core/services/role-permission.service';
import { AuthService } from './core/services/auth.service';
import { createErrorHandler, TraceService } from '@sentry/angular';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([AuthInterceptor])),
    provideAngularSvgIcon(),
    provideAnimations(),
    provideNzConfig({
      message: { nzMaxStack: 1 },
    }),
    importProvidersFrom(NzDrawerModule),
    NzModalService,
    {
      provide: ErrorHandler,
      useValue: createErrorHandler({ logErrors: true }),
    },
    {
      provide: APP_INITIALIZER,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      useFactory: () => () => {},
      deps: [TraceService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory:
        (rbacService: RolePermissionService, auth: AuthService) => () => {
          const role = localStorage.getItem('user_role');
          if (role) {
            try {
              const roles = auth.decryptData(role);
              if (roles && Array.isArray(roles)) {
                // Wait for permissions to load before bootstrap completes
                // This prevents guards from executing before permissions are available
                return new Promise<void>(resolve => {
                  rbacService.loadPermissions(roles).subscribe({
                    next: () => {
                      resolve();
                    },
                    error: error => {
                      console.warn('Failed to load permissions:', error);
                      // Resolve anyway to avoid blocking bootstrap indefinitely
                      resolve();
                    },
                  });

                  // Timeout failsafe: resolve after 5 seconds max
                  setTimeout(() => {
                    resolve();
                  }, 5000);
                });
              }
            } catch (error) {
              console.warn('Failed to decrypt user role data:', error);
              localStorage.removeItem('user_role');
            }
          }
          return Promise.resolve();
        },
      deps: [RolePermissionService, AuthService],
      multi: true,
    },
  ],
};
